// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { getReferenceString } from '@medplum/core';
import type { BundleEntry, Communication } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { isRead } from '@polarissleep/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ThreadBucket } from '../utils/communication-search';

export interface UseThreadInboxOptions {
  query: string;
  threadId: string | undefined;
  recipientFilter?: string;
  bucket?: ThreadBucket;
}

export type ThreadEntry = [Communication, Communication | undefined];

export interface UseThreadInboxReturn {
  loading: boolean;
  error: Error | null;
  // Tuple: [Parent Thread, Last Message in Thread (optional)]
  threadMessages: ThreadEntry[];
  selectedThread: Communication | undefined;
  total: number | undefined;
  unreadCount: number;
  currentUserRef: string | undefined;
  setSelectedThread: React.Dispatch<React.SetStateAction<Communication | undefined>>;
  setThreadMessages: React.Dispatch<React.SetStateAction<ThreadEntry[]>>;
  setError: React.Dispatch<React.SetStateAction<Error | null>>;
  refreshThreadMessages: () => Promise<void>;
}

function lastMessageIsUnreadFor(currentUserRef: string | undefined, lastMessage: Communication | undefined): boolean {
  if (!lastMessage || !currentUserRef) {
    return false;
  }
  if (lastMessage.sender?.reference === currentUserRef) {
    return false;
  }
  return !isRead(lastMessage);
}

function lastMessageSentBy(currentUserRef: string | undefined, lastMessage: Communication | undefined): boolean {
  if (!lastMessage || !currentUserRef) {
    return false;
  }
  return lastMessage.sender?.reference === currentUserRef;
}

/*
useThreadInbox fetches Communication threads (parents with at least one reply) and
classifies them into buckets based on the last message:

  - 'unread': last message is from someone else AND has no notification-readAt extension
  - 'sent':   last message was sent by the current user
  - 'all':    everything (no filter)

Returns the bucket-filtered list plus an `unreadCount` over the unfiltered set,
intended for badge consumers.
*/
export function useThreadInbox({
  query,
  threadId,
  recipientFilter,
  bucket = 'all',
}: UseThreadInboxOptions): UseThreadInboxReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const currentUserRef = useMemo(() => (profile ? getReferenceString(profile) : undefined), [profile]);

  const [loading, setLoading] = useState(true);
  const [allThreads, setAllThreads] = useState<ThreadEntry[]>([]);
  const [selectedThread, setSelectedThread] = useState<Communication | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState<number | undefined>(undefined);

  const fetchAllCommunications = useCallback(async (): Promise<void> => {
    const searchParams = new URLSearchParams(query);
    searchParams.append('identifier:not', 'ai-message-topic');
    searchParams.append('part-of:missing', 'true');
    searchParams.append('_has:Communication:part-of:_id:not', 'null');

    // 'all' bucket means truly all — include completed/stopped threads, not just active ones.
    if (bucket === 'all') {
      searchParams.delete('status');
    }

    if (recipientFilter) {
      searchParams.append('recipient', recipientFilter);
    }

    const bundle = await medplum.search('Communication', searchParams.toString(), { cache: 'no-cache' });
    const parents: Communication[] =
      bundle.entry
        ?.map((entry: BundleEntry) => entry.resource as Communication | undefined)
        .filter((r: Communication | undefined): r is Communication => r !== undefined) || [];

    if (bundle.total !== undefined) {
      setTotal(bundle.total);
    }

    if (parents.length === 0) {
      setAllThreads([]);
      return;
    }

    const queryParts = parents.map((parent: Communication) => {
      const safeId = parent.id?.replace(/-/g, '') || '';
      const alias = `thread_${safeId}`;
      const ref = getReferenceString(parent);

      return `
          ${alias}: CommunicationList(
            part_of: "${ref}"
            _sort: "-sent"
            _count: 1
          ) {
            id
            meta {
              lastUpdated
            }
            partOf {
              reference
            }
            sender {
              display
              reference
            }
            payload {
              contentString
            }
            sent
            status
            extension {
              url
              valueDateTime
            }
          }
        `;
    });

    const fullQuery = `
        query {
          ${queryParts.join('\n')}
        }
      `;

    const response = await medplum.graphql(fullQuery);

    const threadsWithReplies: ThreadEntry[] = parents
      .map((parent: Communication): ThreadEntry | undefined => {
        const safeId = parent.id?.replace(/-/g, '') || '';
        const alias = `thread_${safeId}`;
        const childList = response.data[alias] as Communication[] | undefined;
        const lastMessage = childList && childList.length > 0 ? childList[0] : undefined;
        if (!lastMessage) {
          return undefined;
        }
        return [parent, lastMessage];
      })
      .filter((thread: ThreadEntry | undefined): thread is ThreadEntry => thread !== undefined);

    setAllThreads(threadsWithReplies);
  }, [medplum, query, recipientFilter, bucket]);

  useEffect(() => {
    setLoading(true);
    fetchAllCommunications()
      .catch((err: Error) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchAllCommunications]);

  const threadMessages = useMemo<ThreadEntry[]>(() => {
    if (bucket === 'unread') {
      return allThreads.filter(([, last]) => lastMessageIsUnreadFor(currentUserRef, last));
    }
    if (bucket === 'sent') {
      return allThreads.filter(([, last]) => lastMessageSentBy(currentUserRef, last));
    }
    return allThreads;
  }, [allThreads, bucket, currentUserRef]);

  const unreadCount = useMemo(
    () => allThreads.filter(([, last]) => lastMessageIsUnreadFor(currentUserRef, last)).length,
    [allThreads, currentUserRef]
  );

  useEffect(() => {
    const fetchThread = async (): Promise<void> => {
      if (threadId) {
        const thread = allThreads.find((t) => t[0].id === threadId);
        if (thread) {
          setSelectedThread(thread[0]);
        } else {
          try {
            const communication: Communication = await medplum.readResource('Communication', threadId);

            if (communication.partOf === undefined) {
              setSelectedThread(communication);
            } else {
              const parentRef = communication.partOf[0].reference;
              if (parentRef) {
                const parent = await medplum.readReference({ reference: parentRef } as any);
                setSelectedThread(parent as Communication);
              }
            }
          } catch (err) {
            setError(err as Error);
          }
        }
      } else {
        setSelectedThread(undefined);
      }
    };

    fetchThread().catch((err) => {
      setError(err as Error);
    });
  }, [threadId, allThreads, medplum]);

  const setThreadMessages: React.Dispatch<React.SetStateAction<ThreadEntry[]>> = useCallback((value) => {
    setAllThreads((prev) =>
      typeof value === 'function' ? (value as (p: ThreadEntry[]) => ThreadEntry[])(prev) : value
    );
  }, []);

  return {
    loading,
    error,
    threadMessages,
    selectedThread,
    total,
    unreadCount,
    currentUserRef,
    setSelectedThread,
    setThreadMessages,
    setError,
    refreshThreadMessages: fetchAllCommunications,
  };
}
